<template>
  <div>
    <div id="chart-container" style="position: relative;">
      <HighChart ref="highchart" :options="chartOptions" />
      <div v-if="loading" class="spinner" style="position: absolute; top: 55%; left: 50%; transform: translate(-50%, -50%);"></div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import PointAlarmsPrototypeService from '@/services/r8/PointAlarmsPrototypeService'
import moment from 'moment'
import Highcharts from 'highcharts'

export default {
  name: 'AlarmPreviewTrend',
  components: {
    HighChart: Chart
  },
  props: {
    asset: {
      required: true
    },
    r8token: {
      required: true
    },
    assetAlgorithm: {
      required: true
    },
    startDate: {
      required: true
    },
    refreshPreview: {
      required: true
    },
    unitOfMeasure: {
      required: true
    },
    graphMode: {
      required: true
    },
    sectionType: {
      required: true
    },
    direction: {
      required: true
    }
  },
  data () {
    return {
      loading: false,
      chartOptions: this.getChartOptions(this),
      plotData: null,
      previousIndex: -1
    }
  },
  computed: {

  },
  mounted: async function () {
    this.setInformation('')
    await this.getData(this.asset, this.assetAlgorithm, this.startDate, this.sectionType)
  },
  methods: {
    getChartOptions (t) {
      return {
        chart: {
          zoomType: 'xy',
          animation: false,
          height: '250px',
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },

        credits: {
          enabled: false
        },

        accessibility: {
          enabled: false
        },

        title: {
          text: ''
        },

        boost: {
          enabled: true
        },

        legend: {
          enabled: true
        },

        plotOptions: {
          line: {
            point: {
              events: {
                click: (event) => this.handlePointClick(event)
              }
            }
          }
        },

        tooltip: {
          formatter: function () {
            // Check the type of the series
            if (this.series.type === 'line') {
              return '<b>' + moment(this.point.time).format('DD-MM-YYYY HH:mm:ss') + '</b><br/>' +
                t.$t('views.switch.alarmsettings.value') + ': ' + this.y.toFixed(2) + ' ' + t.unitOfMeasure + '<br/>' +
                t.$t('views.switch.alarmsettings.direction') + ': ' +
                ((this.series.name === 'Normal') ? t.$t('views.switch.alarmsettings.normal') : t.$t('views.switch.alarmsettings.reverse'))
            }
          }
        },

        lang: {
          noData: this.$t('views.switch.nodata'),
          viewFullscreen: this.$t('charts.exporting.fullscreen'),
          printChart: this.$t('charts.exporting.printchart'),
          downloadPNG: this.$t('charts.exporting.downloadpng'),
          downloadJPEG: this.$t('charts.exporting.downloadjpeg'),
          downloadPDF: this.$t('charts.exporting.downloadpdf'),
          downloadSVG: this.$t('charts.exporting.downloadsvg'),
          downloadCSV: this.$t('charts.exporting.downloadcsv'),
          downloadXLS: this.$t('charts.exporting.downloadxls'),
          viewData: this.$t('charts.exporting.viewdatatable'),
          hideData: this.$t('charts.exporting.hidedatatable')
        },

        xAxis: {
          type: 'datetime',
          title: {
            enabled: true,
            text: this.$t('views.alarmlist.date')
          }
        },

        yAxis: {
          title: {
            text: ('[' + this.unitOfMeasure + '] ' || '') +
              ((!this.direction) ? t.$t('views.switch.alarmsettings.normal') : t.$t('views.switch.alarmsettings.reverse'))
          },
          plotLines: []
        },

        series: []
      }
    },

    handlePointClick (event) {
      let samples = []
      let sampleInterval = 0

      const pointIndex = event.point.index
      const color = event.point.color
      if (pointIndex !== this.previousIndex) {
        const seriesDirection = event.point.series.options.custom.direction
        if (this.plotData.direction === seriesDirection) {
          samples = this.plotData.switchMeasurements[pointIndex].samples
          sampleInterval = this.plotData.switchMeasurements[pointIndex].sampleInterval
        } else {
          console.log('No match found for selected direction')
        }

        const sampleData = samples.map((value, index) => {
          return [index * sampleInterval, value]
        })

        const areaLowData = this.plotData.referenceCurves
          .filter(entry => entry.direction === this.direction && entry.referenceType === 'AreaLow')
          .flatMap(entry => entry.convertedValues)
          .map((value, index) => [index * sampleInterval, value])

        const areaHighData = this.plotData.referenceCurves
          .filter(entry => entry.direction === this.direction && entry.referenceType === 'AreaHigh')
          .flatMap(entry => entry.convertedValues)
          .map((value, index) => [index * sampleInterval, value])

        const areaRangeData = areaLowData.map((low, index) => {
          const high = areaHighData[index]
          return [low[0], low[1], high ? high[1] : low[1]]
        })

        // Format datetime for title
        const formattedDatetime = moment(event.point.time).format('DD-MM-YYYY HH:mm:ss')
        const directionText = (event.point.series.name === 'Normal')
          ? this.$t('views.switch.alarmsettings.normal')
          : this.$t('views.switch.alarmsettings.reverse')

        const title = `${formattedDatetime} - ${directionText}`
        this.$emit('point-selected', sampleData, color, this.direction, areaRangeData, title)

        this.previousIndex = pointIndex
      }
    },

    async getData (asset, assetAlgorithm, startDate, sectionType) {
      try {
        if (assetAlgorithm === null) {
          return
        }
        this.loading = true
        this.plotData = await PointAlarmsPrototypeService.previewBackgroundWorker(asset, this.r8token, assetAlgorithm, startDate, sectionType)
        this.chartOptions.yAxis.plotLines = []
        let alarmUpValues = []
        let warningUpValues = []
        let warningDownValues = []
        let alarmDownValues = []
        let expectedValues = []
        let trendPlotValues = []

        // Collect all values for y-axis min/max calculation
        let allYValues = []

        // Clean-up graph series
        this.chartOptions.series = []

        // Trendplot alarm levels in case we have variance etc
        if (this.plotData.algorithmReferenceValues !== null && this.plotData.algorithmReferenceValues.length > 0) {
          // Gather the mad score
          const varianceParameter = this.plotData.algorithmReferenceValues.find(x => x.referenceValueType === 'Variance')
          if (!varianceParameter) {
            this.setError('Variance parameter is missing.')
            return
          }
          let madScore = varianceParameter.referenceValue

          // Ensure we have expected values in the data set
          let values = this.plotData.algorithmReferenceValues.filter(
            x => x.referenceValueType === 'ExpectedValue'
          )

          if (!values.length) {
            // Fallback to ExpectedValueOverall if no ExpectedValue is present
            values = this.plotData.algorithmReferenceValues.filter(
              x => x.referenceValueType === 'ExpectedValueOverall'
            )
          }

          if (!values.length) {
            this.setError('ExpectedValue parameter is missing.')
            return
          }

          let referenceValue = values[0].referenceValue
          let value

          // Alarm multiplier up
          const alarmMultiplierUp = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'AlarmMultiplierUp')
          if (!alarmMultiplierUp) {
            this.setError('AlarmMultiplierUp parameter is missing.')
            return
          }
          value = referenceValue * (1 + madScore * parseInt(alarmMultiplierUp.parameterValue))
          this.chartOptions.yAxis.plotLines.push(
            {
              color: 'red',
              dashStyle: 'Solid',
              width: 2,
              value: value
            }
          )
          allYValues.push(value)

          // Warning multiplier up
          const warningMultiplierUp = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'WarningMultiplierUp')
          if (!warningMultiplierUp) {
            this.setError('WarningMultiplierUp parameter is missing.')
            return
          }
          value = referenceValue * (1 + madScore * parseInt(warningMultiplierUp.parameterValue))
          this.chartOptions.yAxis.plotLines.push(
            {
              color: 'orange',
              dashStyle: 'Solid',
              width: 2,
              value: value
            }
          )
          allYValues.push(value)

          // Expected values
          value = referenceValue
          this.chartOptions.yAxis.plotLines.push(
            {
              color: 'lime',
              dashStyle: 'Solid',
              width: 2,
              value: value
            }
          )
          allYValues.push(value)

          // Warning multiplier down
          const warningMultiplierDown = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'WarningMultiplierDown')
          if (!warningMultiplierDown) {
            this.setError('WarningMultiplierDown parameter is missing.')
            return
          }
          value = referenceValue * (1 - madScore * parseInt(warningMultiplierDown.parameterValue))
          this.chartOptions.yAxis.plotLines.push(
            {
              color: 'orange',
              dashStyle: 'Solid',
              width: 2,
              value: value
            }
          )
          allYValues.push(value)

          // Alarm multiplier down
          const alarmMultiplierDown = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'AlarmMultiplierDown')
          if (!alarmMultiplierDown) {
            this.setError('AlarmMultiplierDown parameter is missing.')
            return
          }
          value = referenceValue * (1 - madScore * parseInt(alarmMultiplierDown.parameterValue))
          this.chartOptions.yAxis.plotLines.push(
            {
              color: 'red',
              dashStyle: 'Solid',
              width: 2,
              value: value
            }
          )
          allYValues.push(value)
        } else {
          // Alarm threshold up
          const alarmThresholdUp = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'AlarmThresholdUp' || x.parameterName === 'BumpAlarmThreshold')
          if (alarmThresholdUp) {
            const value = parseFloat(alarmThresholdUp.parameterValue)
            this.chartOptions.yAxis.plotLines.push(
              {
                color: 'red',
                dashStyle: 'Solid',
                width: 3,
                value: value
              }
            )
            allYValues.push(value)
          }

          // Warning threshold up
          const warningThresholdUp = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'WarningThresholdUp' || x.parameterName === 'BumpWarningThreshold')
          if (warningThresholdUp) {
            const value = parseFloat(warningThresholdUp.parameterValue)
            this.chartOptions.yAxis.plotLines.push(
              {
                color: 'orange',
                dashStyle: 'Solid',
                width: 3,
                value: value
              }
            )
            allYValues.push(value)
          }

          // Warning threshold down
          const warningThresholdDown = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'WarningThresholdDown')
          if (warningThresholdDown) {
            const value = parseFloat(warningThresholdDown.parameterValue)
            this.chartOptions.yAxis.plotLines.push(
              {
                color: 'orange',
                dashStyle: 'Solid',
                width: 3,
                value: value
              }
            )
            allYValues.push(value)
          }

          // Alarm threshold down
          const alarmThresholdDown = this.assetAlgorithm.assetAlgorithmParameters.find(x => x.parameterName === 'AlarmThresholdDown')
          if (alarmThresholdDown) {
            const value = parseFloat(alarmThresholdDown.parameterValue)
            this.chartOptions.yAxis.plotLines.push(
              {
                color: 'red',
                dashStyle: 'Solid',
                width: 3,
                value: value
              }
            )
            allYValues.push(value)
          }
        }

        if (this.plotData.switchMeasurements.length === 0) {
          this.setInformation('No measurements found.')
          return
        }

        this.plotData.switchMeasurements.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
        for (const [index, m] of this.plotData.switchMeasurements.entries()) {
          trendPlotValues.push({
            x: new Date(m.dateTime).getTime(),
            y: m.featureValue,
            time: m.dateTime,
            index: index
          })
          allYValues.push(m.featureValue)
        }

        // Populate the trend graph
        this.chartOptions.series.push({
          name: (this.assetAlgorithm.direction === false) ? this.$t('views.switch.alarmsettings.normal') : this.$t('views.switch.alarmsettings.reverse'),
          data: trendPlotValues,
          type: 'line',
          color: (this.assetAlgorithm.direction === false) ? '#2caffe' : 'darkgreen',
          marker: {
            radius: 2
          },
          turboThreshold: 100000,
          showInLegend: true,
          custom: {
            direction: this.assetAlgorithm.direction
          }
        })

        // Draw the thresholds when data is available
        if (alarmUpValues.length > 0) {
          this.chartOptions.series.push({
            name: 'Alarm threshold up',
            data: alarmUpValues,
            type: 'line',
            lineWidth: 2,
            color: 'red',
            marker: {
              enabled: false
            },
            showInLegend: false
          })
        }
        if (warningUpValues.length > 0) {
          this.chartOptions.series.push({
            name: 'Warning threshold up',
            data: warningUpValues,
            type: 'line',
            lineWidth: 2,
            color: 'orange',
            marker: {
              'enabled': false
            },
            showInLegend: false
          })
        }
        if (expectedValues.length > 0) {
          this.chartOptions.series.push({
            name: 'Expected',
            data: expectedValues,
            type: 'line',
            lineWidth: 2,
            color: 'lime',
            marker: {
              enabled: false
            },
            showInLegend: false
          })
        }
        if (warningDownValues.length > 0) {
          this.chartOptions.series.push({
            name: 'Warning threshold down',
            data: warningDownValues,
            type: 'line',
            lineWidth: 2,
            color: 'orange',
            marker: {
              enabled: false
            },
            showInLegend: false
          })
        }
        if (alarmDownValues.length > 0) {
          this.chartOptions.series.push({
            name: 'Alarm threshold down',
            data: alarmDownValues,
            type: 'line',
            lineWidth: 2,
            color: 'red',
            marker: {
              enabled: false
            },
            showInLegend: false
          })
        }

        const minY = Math.min(...allYValues)
        const maxY = Math.max(...allYValues)
        this.chartOptions.yAxis.min = minY
        this.chartOptions.yAxis.max = maxY
      } catch (err) {
        console.log(err)
        this.setError(err.response.data || err.message)
      } finally {
        this.loading = false
      }
    },

    setError (message) {
      this.$emit('error', message)
      this.loading = false
    },

    setInformation (message) {
      this.$emit('information', message)
      this.loading = false
    }
  },
  watch: {
    async refreshPreview () {
      await this.getData(this.asset, this.assetAlgorithm, this.startDate, this.sectionType)

      // Get all Y values from all series
      // const allYValues = this.chartOptions.series.reduce((acc, series) => {
      //   const seriesYValues = series.data.map(point => point.y)
      //   return acc.concat(seriesYValues)
      // }, [])

      // Calculate min and max Y values and set extremes (auto scale for y-axis wasn't working with scatterplot and line series combined)
      // const minY = Math.min(...allYValues)
      // const maxY = Math.max(...allYValues)
      // this.$refs.highchart.chart.yAxis[0].setExtremes(minY, maxY)
      // this.$refs.highchart.chart.xAxis[0].setExtremes(null, null)
      // this.$refs.highchart.chart.redraw()
    },

    loading (newVal) {
      if (newVal === true) {
        this.$refs.highchart.chart.showLoading('<div class="spinner"></div>')
      } else {
        this.$refs.highchart.chart.hideLoading()
      }
    }
  }
}
</script>

<style scoped>
.spinner {
    border: 6px solid #f3f3f3; /* Light grey */
    border-top: 6px solid #3498db; /* Blue */
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 2s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.highcharts-loading .spinner {
    border: 6px solid #f3f3f3; /* Light grey */
    border-top: 6px solid #3498db; /* Blue */
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 2s linear infinite;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000 !important; /* Ensure it's above all other elements */
}
</style>



// WEBPACK FOOTER //
// src/components/switch/AlarmPreviewTrend.vue